import { CRLF, RESET, paint, sgr } from "../shared/ansi";
import type { ChatLine, Ctx, ListRow, MessageView, ServerEvent, Vars } from "../shared/protocol";
import type { ScreenName, Skin } from "../shared/skin";

const date = (ms: number) => new Date(ms).toISOString().slice(0, 10);
// Chat times are the reader's own local time, like an IRC client's.
const clock = (ms: number) => {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
};

/** Turns board events into ANSI for the terminal, using the board's skin. */
export class Renderer {
  constructor(private skin: Skin) {}

  frame(ctx: Ctx, events: ServerEvent[]): string {
    return events.map((e) => this.event(ctx, e)).join("");
  }

  private event(ctx: Ctx, e: ServerEvent): string {
    switch (e.t) {
      case "hello":
      case "bye":
        return "";
      case "screen":
        return this.screen(e.name, ctx, e.vars) + CRLF;
      case "menu":
        return this.menu(ctx, e) + CRLF;
      case "say":
        return this.str(e.key, ctx, e.vars) + RESET + CRLF;
      case "prompt":
        return this.str(e.key, ctx, e.vars);
      case "pressed":
        return (e.key === "\r" ? "" : e.key.toUpperCase()) + RESET + CRLF;
      case "list":
        return this.list(ctx, e.rows);
      case "message":
        return CRLF + this.message(e.message) + CRLF;
      case "who":
        return this.who(ctx, e.callers);
      case "chat":
        return this.chatLine(ctx, e.line);
      case "chatlog":
        return e.lines.map((l) => this.chatLine(ctx, l)).join("");
      case "vault":
        // Vault events carry ciphertext and keys; the vault session handles them.
        return "";
      case "text":
        // Never painted: whatever braces it contains are just braces.
        return e.text.replace(/\r?\n/g, "\r\n");
      case "names":
        return this.names(ctx, e.people);
    }
  }

  private menu(ctx: Ctx, e: Extract<ServerEvent, { t: "menu" }>): string {
    const { skin } = this;
    const areas = skin.areas
      .map((a, i) =>
        e.locked[i]
          ? paint(`  {dim}[${i + 1}] ${a.name} `, skin.palette) + this.str("lockedArea", ctx, { rung: this.rungName(a.minLevel) })
          : paint(`  {accent}[${i + 1}]{text} ${a.name}`, skin.palette),
      )
      .concat(this.loungeMenu(ctx, e.lounge))
      .concat(e.vault === null ? [] : [this.str(e.vault === "member" ? "vaultMenu" : "vaultDoor", ctx)])
      .join(CRLF);
    const duties = [
      e.pending !== null ? this.str("validateDuty", ctx, { pending: e.pending }) : null,
      e.canSetLevels ? this.str("levelDuty", ctx) : null,
    ]
      .filter((d) => d !== null)
      .map((d) => CRLF + d)
      .join("");
    const readsLeft = e.reads === null ? this.str("readsFree", ctx) : this.str("readsLeft", ctx, { reads: e.reads });
    const postsLeft = e.posts === null ? this.str("postsFree", ctx) : this.str("postsLeft", ctx, { posts: e.posts });
    return this.screen("main", ctx, { areas, duties, rank: this.rungName(e.level), readsLeft, postsLeft });
  }

  private loungeMenu(ctx: Ctx, state: "open" | "locked" | null): string[] {
    const lounge = this.skin.lounge;
    if (!lounge || !state) return [];
    if (state === "open") return [this.str("loungeMenu", ctx)];
    return [paint(`  {dim}[L] ${lounge.name} `, this.skin.palette) + this.str("lockedArea", ctx, { rung: this.rungName(lounge.minLevel) })];
  }

  /** The sysop's handle, for skin text drawn outside an event. */
  get sysop(): string {
    return this.skin.sysop;
  }

  /** One line of skin text, for callers outside the event stream (the vault). */
  text(key: string, ctx: Ctx, vars?: Vars): string {
    return this.str(key, ctx, vars);
  }

  /** One lounge or vault line. Member text is appended raw, after the painted skin text. */
  chatLine(ctx: Ctx, l: ChatLine): string {
    const { dim, text, accent, bright } = this.colors("dim", "text", "accent", "bright");
    const stamp = `${dim}[${clock(l.at)}] `;
    switch (l.kind) {
      case "say":
        return `${stamp}${dim}<${this.nick(l.handle)}${dim}>${text} ${l.text ?? ""}${RESET}${CRLF}`;
      case "me":
        return `${stamp}${accent}* ${this.nick(l.handle)}${text} ${l.text ?? ""}${RESET}${CRLF}`;
      case "join":
        return stamp + this.str("chatJoin", ctx, { who: l.handle }) + RESET + CRLF;
      case "part":
        return stamp + this.str("chatPart", ctx, { who: l.handle }) + RESET + CRLF;
      case "kick":
        return stamp + this.str("chatKick", ctx, { who: l.handle, by: l.by ?? "" }) + RESET + CRLF;
      case "topic":
        return stamp + this.str("chatTopic", ctx, { who: l.handle }) + bright + (l.text ?? "") + RESET + CRLF;
      case "msg": {
        const mine = ctx.handle !== undefined && l.handle.toLowerCase() === ctx.handle.toLowerCase();
        const head = mine ? this.str("chatMsgTo", ctx, { who: l.to ?? "" }) : this.str("chatMsgFrom", ctx, { who: l.handle });
        return stamp + head + text + (l.text ?? "") + RESET + CRLF;
      }
      case "roll":
        return stamp + this.str("chatRoll", ctx, { who: l.handle }) + bright + (l.text ?? "") + RESET + CRLF;
      case "mute":
      case "unmute":
        return stamp + this.str(l.kind === "mute" ? "chatMute" : "chatUnmute", ctx, { who: l.handle, by: l.by ?? "" }) + RESET + CRLF;
    }
  }

  /** IRC-style names: ops get an @, away people get a note. */
  private names(ctx: Ctx, people: Extract<ServerEvent, { t: "names" }>["people"]): string {
    const { accent } = this.colors("accent");
    const sep = paint("{dim}, ", this.skin.palette);
    const list = people.map((p) => {
      const away = p.away === undefined ? "" : this.str("namesAway", ctx, { why: p.away ? `: ${p.away}` : "" });
      return (p.op ? `${accent}@` : "") + this.nick(p.handle) + away;
    });
    return this.str("loungeNames", ctx) + list.join(sep) + RESET + CRLF;
  }

  /** A handle in its own color, picked from the lounge's nick colors by a stable hash. */
  private nick(handle: string): string {
    const colors = this.skin.lounge?.nickColors ?? ["bright-white"];
    let hash = 0;
    for (const ch of handle.toLowerCase()) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
    return sgr(colors[hash % colors.length]) + handle;
  }

  private list(ctx: Ctx, rows: ListRow[]): string {
    const { accent, dim, text, warn } = this.colors("accent", "dim", "text", "warn");
    const pendingTag = this.str("pendingTag", ctx);
    return rows
      .map((m) => {
        const tag = m.pending ? ` ${warn}(${pendingTag})` : "";
        return (
          `  ${accent}#${String(m.id).padEnd(5)}${dim}${date(m.created_at)}  ` +
          `${text}${m.author.padEnd(20)} ${m.subject.slice(0, 40)}${tag}${RESET}${CRLF}`
        );
      })
      .join("");
  }

  /** Member-written text is printed raw, never through paint(). */
  private message(m: MessageView): string {
    const { accent, title, dim, bright, text } = this.colors("accent", "title", "dim", "bright", "text");
    return (
      `  ${accent}#${m.id}  ${title}${m.subject}${CRLF}` +
      `  ${dim}from ${bright}${m.author}${dim} on ${date(m.created_at)}${CRLF}${CRLF}` +
      text + m.body.split("\n").map((l) => `  ${l}`).join(CRLF) + RESET + CRLF
    );
  }

  private who(ctx: Ctx, callers: Extract<ServerEvent, { t: "who" }>["callers"]): string {
    const { dim, text, bright } = this.colors("dim", "text", "bright");
    const calling = this.skin.strings.callingIn ?? "";
    let out = CRLF + this.str("whoTitle", ctx) + RESET + CRLF + CRLF;
    out += `  ${dim}Node  ${"Handle".padEnd(21)}Doing${CRLF}`;
    for (const c of callers) {
      const doing = this.skin.strings[`activity.${c.activity}`] ?? c.activity;
      out += `  ${bright}${String(c.node).padEnd(6)}${text}${(c.handle ?? calling).padEnd(21)}${doing}${RESET}${CRLF}`;
    }
    return out + CRLF;
  }

  private rungName(level: number): string {
    return this.skin.ladder.find((r) => r.level === level)?.name ?? String(level);
  }

  private vars(ctx: Ctx, extra: Vars = {}): Vars {
    const area = this.skin.areas.find((a) => a.id === ctx.area)?.name ?? "";
    return {
      board: this.skin.name, sysop: this.skin.sysop, nodes: this.skin.maxNodes,
      node: ctx.node || "-", handle: ctx.handle ?? "caller", area, lounge: this.skin.lounge?.name ?? "", vault: this.skin.vault?.name ?? "", ...extra,
    };
  }

  private str(key: string, ctx: Ctx, extra?: Vars): string {
    return paint(this.skin.strings[key] ?? key, this.skin.palette, this.vars(ctx, extra));
  }

  private screen(name: ScreenName, ctx: Ctx, extra?: Vars): string {
    return this.skin.screens[name].map((l) => paint(l, this.skin.palette, this.vars(ctx, extra)) + RESET).join(CRLF);
  }

  private colors<K extends string>(...names: K[]): Record<K, string> {
    return Object.fromEntries(names.map((n) => [n, paint(`{${n}}`, this.skin.palette)])) as Record<K, string>;
  }
}
